import type { ReactNode } from "react";
import { UseFormRegister, FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { EMAIL_REG_EXP } from "src/domains/shared/constants/constants";
import { testIds } from "src/test/testIds";
import { AuthLinks, AuthSubmitButton } from "../AuthCard/AuthActions";
import { AuthCard, AuthFields } from "../AuthCard/AuthCard";
import { AuthField, AuthPasswordField } from "../AuthCard/AuthField";

type LoginFormData = {
  email: string;
  password: string;
};

export interface LoginFormProps {
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  register: UseFormRegister<LoginFormData>;
  errors: FieldErrors<LoginFormData>;
  error?: { message: string };
  isLoading?: boolean;
  /** 「회원가입」 링크 주소 */
  signUpHref: string;
  /** 「비밀번호 찾기」 링크 주소 */
  forgotPasswordHref: string;
  /** 로그인 카드 첫 줄(제목 아래·폼 위)에 놓을 안내 — 예: 밀려나 로그아웃된 사유 */
  notice?: ReactNode;
}

/** 로그인 카드(marketing-auth-pages) — 이메일·비밀번호, 「로그인」, 「회원가입」·「비밀번호 찾기」 */
export const LoginForm = ({
  onSubmit,
  register,
  errors,
  error,
  isLoading,
  signUpHref,
  forgotPasswordHref,
  notice,
}: LoginFormProps) => {
  const { t } = useTranslation("auth");

  return (
    <AuthCard title={t("form.login")} notice={notice}>
      {/* 검증은 폼이 정한 문구로 보인다 — 브라우저 기본 말풍선은 끈다 */}
      <form onSubmit={onSubmit} noValidate>
        <AuthFields>
          <AuthField
            data-testid={testIds.login.emailInput}
            label={t("form.email")}
            type="email"
            autoComplete="username"
            placeholder={t("validation.emailRequired")}
            message={errors.email?.message}
            {...register("email", {
              required: t("validation.emailRequired"),
              pattern: {
                value: EMAIL_REG_EXP,
                message: t("validation.emailInvalid"),
              },
            })}
          />
          <AuthPasswordField
            data-testid={testIds.login.passwordInput}
            label={t("form.password")}
            autoComplete="current-password"
            placeholder={t("validation.passwordRequired")}
            message={error?.message ?? errors.password?.message}
            {...register("password", {
              required: t("validation.passwordRequired"),
            })}
          />
        </AuthFields>
        <AuthSubmitButton data-testid={testIds.login.submitBtn} loading={isLoading}>
          {t("form.login")}
        </AuthSubmitButton>
        <AuthLinks
          items={[
            { label: t("form.signUp"), href: signUpHref, testId: testIds.login.signupBtn },
            { label: t("form.forgotPassword"), href: forgotPasswordHref },
          ]}
        />
      </form>
    </AuthCard>
  );
};
